import { Card, Colors, htmlToSlate, Icon, RichText, slateToHtml, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { EDITOR_TOOLBAR_DENSITY } from '@/constants/RichTextEditor';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { CONTEXT_MAX_LENGTH } from '@/pages/ThematicMeetings/constants';

interface ContextFieldProps {
    /** The meeting's current context, as HTML. Like every `RichText` consumer in this codebase,
     * only read once to seed the editor's initial document — see `ObjectiveFormPage`'s own note on
     * why `RichText` cannot be resynced from a changing `value` prop after mount. */
    value: string;
    /** Already-translated validation message, or undefined while the field is valid. */
    errorMessage?: string;
    /** The line under the field: each flow says what the context is used for. */
    helpText: string;
    onChange: (value: string) => void;
}

/**
 * The optional meeting-context editor of the creation flow.
 */
export const ContextField = ({ value, errorMessage, helpText, onChange }: ContextFieldProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    // Read once, on mount, like `RichText` itself (see the prop doc above): ignores every later
    // change to `value`.
    const [initialSlateValue] = useState(() => htmlToSlate(value));

    return (
        <div className={'flex flex-col gap-3'}>
            <Card
                bgColor={'bg-base-25'}
                borderColor={Colors.BASE_100}
                borderRadius={'2xl'}
                padding={'none'}
                // `Card`'s own `padding` prop has no 48 by 28, so the pair is written in
                // `responsive-utilities.css`, where the frame also picks up the module's phone
                // inset -- 48px of side padding leaves a phone barely half its width for the
                // editor inside.
                className={'new-meeting-context-frame page-frame-inset'}
            >
                <RichText
                    toolbarDensity={EDITOR_TOOLBAR_DENSITY}
                    // Inside the creation window the editor shares its height with the step's
                    // other blocks; the module's other composers answer this the same way.
                    contentMinHeight={'compact'}
                    value={initialSlateValue}
                    onChange={(slateValue) => onChange(slateToHtml(slateValue))}
                    placeholder={t('thematicMeetings.newMeeting.contextPlaceholder')}
                    maxLength={CONTEXT_MAX_LENGTH}
                    showCharacterCount={false}
                    surface={'framed'}
                    error={Boolean(errorMessage)}
                    errorMessage={errorMessage}
                    // The step's own title already reads "Meeting context and objectives" above this
                    // card, so the field shows no label of its own. This is what names it: it is the
                    // editor's only name, and dropping it would leave the field anonymous.
                    ariaLabel={t('thematicMeetings.newMeeting.contextLabel')}
                    locale={language}
                    dataTestId={'new-meeting-context-input'}
                />
            </Card>

            <div className={'flex items-center gap-1.5'}>
                <Icon
                    name={'essential-info-circle'}
                    size={'sm'}
                    color={Colors.BASE_500}
                    ariaHidden
                />

                <Text
                    size={'small'}
                    color={Colors.BASE_500}
                >
                    {helpText}
                </Text>
            </div>
        </div>
    );
};
